import { SerializedNodeData, Node, NodeData } from '@candulabs/craft';
import at from 'lodash/at';
import flatten from 'lodash/flatten';
import fromPairs from 'lodash/fromPairs';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import pickBy from 'lodash/pickBy';
import { Text } from 'slate';

import { BuiltInComponents } from 'craft/constants';
import { craftToSlateMarks, slateToCraftMarks } from 'craft/rte/constants';
import { SlateNode } from 'craft/types';

const getSlateMarks = ({ custom }: any) => {
  if (!custom.custom) {
    return {};
  }
  // TODO get rif of this custom.custom madness
  const { marks } = custom.custom;
  if (!marks || !isArray(marks) || marks.length < 1) {
    return {};
  }
  return fromPairs(
    marks.map((mark: keyof typeof craftToSlateMarks) => [craftToSlateMarks[mark], true]),
  );
};

export const craftToSlate = (nodeIds: string[], nodes: { [key: string]: Node }): SlateNode[] => {
  const slateNodes: any = nodeIds.map((nodeId) => {
    const data = nodes[nodeId]?.data || {};
    const { childrenString, ...props } = data.props || {};

    const baseProperties = pickBy({
      id: nodeId,
      type: data.name,
      children: data.nodes && data.nodes.length > 0 ? craftToSlate(data.nodes, nodes) : null,
      data: pickBy({
        variant: at(data, 'custom.custom.variant')[0],
        css: at(data, 'custom.css')[0],
        // Some Slate elements (eg: Link) might need to read the props
        ...(Object.keys(props).length > 0 ? { props } : {}),
      }),
    });
    const textProperties =
      data.name === BuiltInComponents.Text ? { text: childrenString || '' } : {};
    const marks = getSlateMarks(data);
    return { ...baseProperties, ...textProperties, ...marks };
  });
  return slateNodes;
};

// TODO figure out if this type is right
type CraftDataProps = Omit<NodeData, 'displayName' | 'name'> & {
  css: any;
  type: string;
};

const craftData = ({
  type,
  custom,
  css,
  props,
  nodes,
  parent,
  isCanvas,
  linkedNodes,
  hidden,
}: CraftDataProps): SerializedNodeData => ({
  custom: { actions: undefined, css: css || {}, custom: !isEmpty(custom) ? custom : undefined },
  name: type,
  parent,
  nodes,
  props,
  type: { resolvedName: type },
  linkedNodes,
  isCanvas: !!isCanvas,
  hidden: !!hidden,
});

const getCraftMarks = (node: SlateNode) => {
  // only text nodes have marks
  if (!Text.isText(node)) {
    return null;
  }
  const marks = Object.keys(node)
    .map((key) => slateToCraftMarks[key])
    .filter((mark) => !!mark);
  if (marks.length < 1) {
    return null;
  }
  return marks;
};

const specialTypes = {
  UL: 'List',
  OL: 'List',
};

const getType = ({ type, text }: SlateNode) =>
  // TODO better typing here
  // @ts-ignore
  specialTypes[type] || type || (text !== undefined && BuiltInComponents.Text);

const asCraftNode = (node: SlateNode, parent: string): [string, SerializedNodeData] => {
  const { id, text, children } = node;
  const data: Record<string, any> = node.data || {};

  const { variant, css, hidden } = data || ({} as any);
  const marks = getCraftMarks(node);
  const type = getType(node);
  const props = { ...(data.props || {}) };

  if (type === BuiltInComponents.Text && text && text.length) {
    props.childrenString = text;
  }

  // we should find a better way to determine if it's a Canvas node or not
  const isCanvas = type === BuiltInComponents.List;

  return [
    id,
    craftData({
      type,
      custom: pickBy({ variant, marks }),
      css,
      props,
      parent,
      nodes: children ? children.map((child: any) => child.id) : [],
      isCanvas,
      linkedNodes: {},
      hidden,
    }),
  ];
};

const slateNodeToCraftNode = (
  node: SlateNode,
  parentId: string,
): [string, SerializedNodeData][] => {
  const craftNode = asCraftNode(node, parentId);
  if (!node.children) {
    return [craftNode];
  }

  return [
    craftNode,
    // @ts-ignore
    ...flatten(node.children.map((child) => slateNodeToCraftNode(child, node.id))),
  ];
};

export const slateToCraft = (
  nodes: SlateNode[],
  parentId: string,
): Record<string, SerializedNodeData> =>
  fromPairs(flatten(nodes.map((node) => slateNodeToCraftNode(node, parentId))));
